// frontend/src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import GoogleAnalytics from './components/GoogleAnalytics';
import HomePage from './pages/HomePage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import DashboardPage from './pages/DashboardPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ProfilePage from './pages/ProfilePage';
import CommerceAccountPage from './pages/CommerceAccountPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage';
import OfflinePage from './pages/OfflinePage';
import OfflineCentersPage from './pages/OfflineCentersPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import CancellationRefundPage from './pages/CancellationRefundPage';
import LegalDocumentPage from './pages/LegalDocumentPage.jsx';
import ComingSoon from './components/ComingSoon';
import ProtectedRoute from './components/ProtectedRoute';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';

import AboutUs from "./pages/AboutUs";


// Live Program imports
import LiveWebinarsPage from './pages/LiveWebinarsPage';
import LiveMentorshipPage from './pages/LiveMentorshipPage';
import LiveWorkshopsPage from './pages/LiveWorkshopsPage';
import LiveGuidancePage from './pages/LiveGuidancePage';
import LiveProgramDetailPage from './pages/LiveProgramDetailPage';
import LiveProgramCheckoutPage from './pages/LiveProgramCheckoutPage';

// Investment Pages
import { 
  MutualFundsPage, 
  DigitalGoldPage, 
  FixedDepositsPage, 
  InsurancePage, 
  AICalculatorPage 
} from './pages/investment';

const BackendAdminRedirect = ({ path }) => {
  React.useEffect(() => {
    const apiOrigin = new URL(import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').origin;
    window.location.replace(`${apiOrigin}${path}`);
  }, [path]);
  return <div className="min-h-screen bg-black pt-32 text-center text-gray-300" role="status">Opening Laravel administration…</div>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-black text-white flex flex-col">
          <GoogleAnalytics />
          <Navbar />
          <main className="flex-grow">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/:slug" element={<CourseDetailPage />} />
              <Route path="/subscriptions" element={<SubscriptionPlansPage />} />
              <Route path="/checkout/:slug" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
              <Route path="/sign-in/*" element={<SignInPage />} />
              <Route path="/sign-up/*" element={<SignUpPage />} />
              
              {/* Live Program Routes - Public */}
              <Route path="/live/webinars" element={<LiveWebinarsPage />} />
              <Route path="/live/mentorship" element={<LiveMentorshipPage />} />
              <Route path="/live/workshops" element={<LiveWorkshopsPage />} />
              <Route path="/live/guidance" element={<LiveGuidancePage />} />
              <Route path="/live/:type/:slug" element={<LiveProgramDetailPage />} />
              <Route path="/live-checkout/:type/:slug" element={<ProtectedRoute><LiveProgramCheckoutPage /></ProtectedRoute>} />
              
              {/* Protected User Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route path="/account/commerce" element={<ProtectedRoute><CommerceAccountPage /></ProtectedRoute>} />
              
              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin" /></ProtectedRoute>}
              />
              <Route 
                path="/admin/dashboard" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin" /></ProtectedRoute>}
              />
              <Route 
                path="/admin/courses/add" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin/courses/create" /></ProtectedRoute>}
              />
              <Route 
                path="/admin/courses/edit/:id" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin/courses" /></ProtectedRoute>}
              />
              
              {/* Admin Blog Routes */}
              <Route 
                path="/admin/blogs" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin/blog" /></ProtectedRoute>}
              />
              
              {/* Admin Live Program Routes */}
              <Route 
                path="/admin/live-programs" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin/live-programs" /></ProtectedRoute>}
              />
              <Route 
                path="/admin/live-programs/add" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin/live-programs/create" /></ProtectedRoute>}
              />
              <Route 
                path="/admin/live-programs/edit/:id" 
                element={<ProtectedRoute adminOnly><BackendAdminRedirect path="/admin/live-programs" /></ProtectedRoute>}
              />
              
              {/* Payment Routes */}
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/payment/failed" element={<PaymentFailed />} />
              
              {/* Offline Program Routes */}
              <Route path="/offline" element={<OfflinePage />} />
              <Route path="/offline/centers" element={<OfflineCentersPage />} />
              <Route path="/offline/centers/:slug" element={<OfflineCentersPage />} />
              
              {/* Support Routes */}
              <Route path="/cancellation-refund" element={<CancellationRefundPage />} />
              
              <Route path="/privacy" element={<LegalDocumentPage type="privacy" fallbackTitle="Privacy Policy" />} />
              <Route path="/terms" element={<LegalDocumentPage type="terms" fallbackTitle="Terms and Conditions" />} />

                   <Route path="/blog" element={<BlogPage />} />
                   <Route path="/blog/:slug" element={<BlogDetailPage />} />
                   




                   <Route path="/about" element={<AboutUs/>} />







              {/* Investment Routes */}
              <Route path="/investment" element={<MutualFundsPage />} />
              <Route path="/investment/mutual-funds" element={<MutualFundsPage />} />
              <Route path="/investment/digital-gold" element={<DigitalGoldPage />} />
              <Route path="/investment/fixed-deposits" element={<FixedDepositsPage />} />
              <Route path="/investment/insurance" element={<InsurancePage />} />
              <Route path="/investment/ai-calculator" element={<AICalculatorPage />} />

              {/* Coming Soon Routes */}
              <Route path="/classroom" element={<ComingSoon pageName="Classroom Program" />} />
              <Route path="/webinars" element={<ComingSoon pageName="Webinars" />} />
              <Route path="/mentorship" element={<ComingSoon pageName="Mentorship" />} />
              {/* <Route path="/about" element={<ComingSoon pageName="About Us" />} /> */}
              <Route path="/contact" element={<ComingSoon pageName="Contact Us" />} />
              <Route path="/faq" element={<ComingSoon pageName="FAQ" />} />
              {/* <Route path="/privacy" element={<ComingSoon pageName="Privacy Policy" />} />
              <Route path="/terms" element={<ComingSoon pageName="Terms & Conditions" />} /> */}
              
              {/* 404 */}
              <Route path="*" element={<ComingSoon pageName="Page Not Found" />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
